// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import get from "lodash-es/get";
import intersection from "lodash-es/intersection";

import { flatten } from "utils/serialize";

import { ROLES } from "./roles";

export const allowInOrganization =
  (organizationId, allowedRoles) =>
  ({ activeOrganization = {} } = {}) => {
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }
    if (!activeOrganization) {
      return false;
    }
    const sameOrganization = activeOrganization.id === organizationId;
    const hasRequiredRole =
      intersection(allowedRoles, activeOrganization.roles).length > 0;

    return sameOrganization && hasRequiredRole;
  };

export const checkAccess = (values, allowed) => {
  if (!allowed || allowed.length === 0) {
    return true;
  }

  const roles = get(values, "activeOrganization.roles", []);

  return allowed.some((roleOrCheck) => {
    if (typeof roleOrCheck === "function") {
      return roleOrCheck(values);
    } else {
      return roles.includes(roleOrCheck);
    }
  });
};

export const checkOrganizationTypeAccess = (types, allowedTypes) => {
  if (!allowedTypes || allowedTypes.length === 0) {
    return true;
  }

  return allowedTypes.some((type) => types.includes(type));
};

export const isAllowed = (user, roles) => {
  if (!roles || roles.length === 0 || !user) {
    return true;
  }
  return roles.every((requiredRole) =>
    user.authorizations.some((auth) =>
      auth.grants.some((grant) => grant.role === requiredRole)
    )
  );
};

export const isAllowedToAddRole = (currentUserRoles, editUserRoles, role) => {
  const currentUserIsOwnerOrAdmin = currentUserRoles.some((role) =>
    [ROLES.organization.owner, ROLES.administrator.userAdmin].includes(role)
  );
  const currentUserCanEditUsers = currentUserRoles.some((role) =>
    [
      ROLES.organization.owner,
      ROLES.organization.roleManager,
      ROLES.administrator.userAdmin,
    ].includes(role)
  );
  if (!currentUserCanEditUsers) {
    return false;
  }

  // only org owner or admin can assign org owner
  // @todo most probably this should be extended to admin role
  //  (but this is how it works currently in the backend)
  //  [ROLES.organization.owner, ROLES.administrator.userAdmin].includes(role)
  return !(ROLES.organization.owner === role && !currentUserIsOwnerOrAdmin);
};

const impliedRoles = {
  [ROLES.organization.owner]: {
    implies: Object.values(flatten(ROLES)).filter(
      (role) => role !== ROLES.organization.owner
    ),
  },
};

const isRoleImpliedByUserRole = (role, editUserRoles) =>
  Object.entries(impliedRoles)
    .filter(([roleName]) => editUserRoles.includes(roleName))
    .some(([, roleDetails]) => roleDetails.implies.includes(role));

export const isAllowedToRemoveRole = (
  currentUserRoles,
  editUserRoles,
  role
) => {
  const editUserIsOwner = editUserRoles.includes(ROLES.organization.owner);
  const currentUserIsOwnerOrAdmin = currentUserRoles.some((role) =>
    [ROLES.organization.owner, ROLES.administrator.userAdmin].includes(role)
  );
  const currentUserCanEditUsers = currentUserRoles.some((role) =>
    [
      ROLES.organization.owner,
      ROLES.organization.roleManager,
      ROLES.administrator.userAdmin,
    ].includes(role)
  );
  if (!currentUserCanEditUsers) {
    return false;
  }

  if (editUserIsOwner && !currentUserIsOwnerOrAdmin) {
    return false;
  }

  return !isRoleImpliedByUserRole(role, editUserRoles);
};

export const isSignatoryUser = (organizations, currentUser) => {
  return organizations[currentUser.activeOrganizationId].roles.includes(
    ROLES.provider.signatory
  );
};

export const isModerator = (organizations, currentUser) => {
  return organizations[currentUser.activeOrganizationId].roles.includes(
    ROLES.moderator.moderator
  );
};
